import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Megaphone, Plus, X, Eye, MousePointerClick } from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { ListingPicker, type PickedListing } from './ListingPicker';

const PAGES = ['buy', 'rent', 'projects', 'plots', 'commercial'];

const detailUrlFor = (l: PickedListing) => {
  switch (l.type) {
    case 'property': return `/properties/${l.id}`;
    case 'commercial': return `/commercial/${l.id}`;
    case 'plot': return `/plots/${l.id}`;
    case 'project': return `/projects/${l.id}`;
  }
};

export function AdCampaignsManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [linkedListing, setLinkedListing] = useState<PickedListing | null>(null);
  const [form, setForm] = useState({
    advertiser_name: '',
    advertiser_contact: '',
    title: '',
    description: '',
    image_url: '',
    target_url: '',
    placement: 'left_sidebar' as 'left_sidebar' | 'right_sidebar' | 'property_detail' | 'homepage_banner',
    pages: ['buy', 'rent'] as string[],
    tier: 'standard' as 'standard' | 'premium',
    days: 30,
    price_paid: '',
    payment_ref: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ad-campaigns'],
    queryFn: async () => {
      const { data } = await supabase.from('ad_campaigns').select('*').order('created_at', { ascending: false });
      return data || [];
    },
  });

  const togglePage = (p: string) => {
    setForm(f => ({ ...f, pages: f.pages.includes(p) ? f.pages.filter(x => x !== p) : [...f.pages, p] }));
  };

  const submit = async () => {
    if (!user) return;
    if (!form.advertiser_name || !form.title || !form.image_url) {
      toast.error('Advertiser, title and image URL are required');
      return;
    }
    const endDate = addDays(new Date(), form.days);
    const { error } = await supabase.from('ad_campaigns').insert({
      advertiser_name: form.advertiser_name,
      advertiser_contact: form.advertiser_contact || null,
      title: form.title,
      description: form.description || null,
      image_url: form.image_url,
      target_url: form.target_url || null,
      placement: form.placement,
      pages: form.pages,
      tier: form.tier,
      end_date: endDate.toISOString().slice(0, 10),
      price_paid: parseFloat(form.price_paid) || 0,
      payment_ref: form.payment_ref || null,
      created_by: user.id,
    });
    if (error) { toast.error(error.message); return; }
    toast.success('Campaign created');
    qc.invalidateQueries({ queryKey: ['ad-campaigns'] });
    setOpen(false);
    setLinkedListing(null);
    setForm({ ...form, advertiser_name: '', advertiser_contact: '', title: '', description: '', image_url: '', target_url: '', price_paid: '', payment_ref: '' });
  };

  const deactivate = async (id: string) => {
    const { error } = await supabase.from('ad_campaigns').update({ is_active: false }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Campaign deactivated');
    qc.invalidateQueries({ queryKey: ['ad-campaigns'] });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5" /> Ad Campaigns</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> New Campaign</Button>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-32" /> : !data?.length ? (
          <p className="text-sm text-muted-foreground text-center py-8">No campaigns yet</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Advertiser</TableHead>
                <TableHead>Placement</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right"><Eye className="h-3 w-3 inline" /></TableHead>
                <TableHead className="text-right"><MousePointerClick className="h-3 w-3 inline" /></TableHead>
                <TableHead>Expires</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((c: any) => {
                const daysLeft = differenceInDays(new Date(c.end_date), new Date());
                return (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium text-sm max-w-[160px] truncate">{c.title}</TableCell>
                    <TableCell className="text-xs">{c.advertiser_name}</TableCell>
                    <TableCell className="text-xs capitalize">{c.placement.replace('_', ' ')}</TableCell>
                    <TableCell><Badge variant={c.tier === 'premium' ? 'default' : 'secondary'}>{c.tier}</Badge></TableCell>
                    <TableCell className="text-right text-xs">{c.impressions.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs">{c.clicks.toLocaleString()}</TableCell>
                    <TableCell className="text-xs">{format(new Date(c.end_date), 'MMM d')} ({daysLeft}d)</TableCell>
                    <TableCell>
                      {c.is_active && <Button variant="ghost" size="sm" onClick={() => deactivate(c.id)}><X className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Ad Campaign</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Linked Listing (optional)</Label>
              <ListingPicker
                value={linkedListing}
                onChange={(l) => {
                  setLinkedListing(l);
                  if (l) {
                    setForm(f => ({
                      ...f,
                      target_url: f.target_url || `${window.location.origin}${detailUrlFor(l)}`,
                      title: f.title || l.title,
                      image_url: f.image_url || l.image_url || '',
                    }));
                  }
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">Picks auto-fill Title, Image and Target URL below (you can override).</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Advertiser Name *</Label><Input value={form.advertiser_name} onChange={e => setForm({ ...form, advertiser_name: e.target.value })} /></div>
              <div><Label>Advertiser Contact</Label><Input value={form.advertiser_contact} onChange={e => setForm({ ...form, advertiser_contact: e.target.value })} /></div>
            </div>
            <div><Label>Ad Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Image URL *</Label><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
              <div><Label>Target URL</Label><Input value={form.target_url} onChange={e => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Placement</Label>
                <Select value={form.placement} onValueChange={(v: any) => setForm({ ...form, placement: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left_sidebar">Left Sidebar</SelectItem>
                    <SelectItem value="right_sidebar">Right Sidebar</SelectItem>
                    <SelectItem value="property_detail">Property Detail</SelectItem>
                    <SelectItem value="homepage_banner">Homepage Banner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tier</Label>
                <Select value={form.tier} onValueChange={(v: any) => setForm({ ...form, tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Show on Pages</Label>
              <div className="flex flex-wrap gap-3">
                {PAGES.map(p => (
                  <div key={p} className="flex items-center gap-2">
                    <Checkbox id={`pg-${p}`} checked={form.pages.includes(p)} onCheckedChange={() => togglePage(p)} />
                    <Label htmlFor={`pg-${p}`} className="font-normal capitalize cursor-pointer">{p}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Days</Label><Input type="number" value={form.days} onChange={e => setForm({ ...form, days: Number(e.target.value) })} /></div>
              <div><Label>Price (ETB)</Label><Input type="number" value={form.price_paid} onChange={e => setForm({ ...form, price_paid: e.target.value })} /></div>
              <div><Label>Payment Ref</Label><Input value={form.payment_ref} onChange={e => setForm({ ...form, payment_ref: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
